import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {requestMoreAccounts} from '../../../client/control_central/accountQuota';
import {CancelSubscriptionSection} from '../CancelSubscriptionSection';
import {
    NS,
    annualMessage,
    annualOffer,
    bankTransferLines,
    billingIdentityMessage,
    currencyKeyOf,
    currentPeriodOf,
    customPlanMessage,
    formatAmount,
    isAutomaticMethod,
    isCouponMethod,
    isCustomPlan,
    listMonthly,
    morePortalMessage,
    moreUsersMessage,
    nextPlanOf,
    overdueOf,
    paymentMethodKey,
    paymentMethodMessage,
    paymentProblemMessage,
    planChangeMessage,
    planDirection,
    planOf,
    priceOf,
    retentionOf,
    subscriptionPillOf,
    usageOf,
} from './subscriptionPageModel';
import {openSupportRequest, recordSubscriptionEvent} from './subscriptionRequests';
import {PlanSummaryCard} from './PlanSummaryCard';
import {UsageCard} from './UsageCard';
import {PaymentsCard} from './PaymentsCard';
import {OverdueNotice} from './OverdueNotice';
import {BillingDetailsCard} from './BillingDetailsCard';
import {AnnualOfferCard, FaqCard} from './SubscriptionAside';
import {PlanChooser} from './PlanChooser';
import {RequestConfirmModal} from './RequestConfirmModal';
import {NotifyPaymentDialog} from './NotifyPaymentDialog';

// El destino de los links que ya existen (`ClientUsersLimitNotice`, `MemoryArchiveView`) y del «Ver los
// planes» del modal de baja. En la agencia paga abre «Cambiar de plan» en la misma pantalla, y «Atrás»
// vuelve al resumen.
export const PLAN_CHANGE_HASH = '#plan-change';

function hashWantsChooser() {
    return typeof window !== 'undefined' && window.location.hash === PLAN_CHANGE_HASH;
}

/**
 * La vista de la agencia PAGA (ni prueba ni vencida) de Agencia › Suscripción, tanda «Suscripción y pagos»
 * (UD-16, mockups 1, 2, 3, 5 y 6). Que la agencia se informe, resuelva sola lo que pueda y pida por el chat
 * lo demás, con el pedido escrito.
 *
 * Los datos los pide el padre (`AgencySubscriptionContent`); acá se arma la pantalla. `paymentSubscription`
 * es `{status: 'loading' | 'ok' | 'error', data}`: con error, el próximo cobro no se dibuja.
 */
export function PaidSubscriptionView({subscriptionData, billingData, paymentSubscription, isSuperAdmin,
                                         cancellationRequest, onCancellationChanged, refreshingBilling,
                                         onRetryBilling, onRefreshBilling, onBillingEmailsSaved,
                                         onLiquidationUpdated, onRetentionAccepted, topNotices}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    const rootRef = useRef(null);
    const pushedChooser = useRef(false);
    const [showChooser, setShowChooser] = useState(hashWantsChooser);
    const [request, setRequest] = useState(null);
    const [notifyFor, setNotifyFor] = useState(null);

    useEffect(() => {
        recordSubscriptionEvent('view_paid');
        const sync = () => setShowChooser(hashWantsChooser());
        window.addEventListener('hashchange', sync);
        window.addEventListener('popstate', sync);
        return () => {
            window.removeEventListener('hashchange', sync);
            window.removeEventListener('popstate', sync);
        };
    }, []);

    // Al pasar del resumen a «Cambiar de plan» (y de vuelta), arriba de todo. NO al abrir la pantalla: en el
    // clásico dejaba el encabezado y las solapas de la agencia fuera de la vista (C-sp-fe-04).
    // Se compara contra el valor anterior y no contra «primer render»: el modo estricto de React monta dos veces.
    const lastChooser = useRef(showChooser);
    useEffect(() => {
        if (lastChooser.current === showChooser) return;
        lastChooser.current = showChooser;
        if (rootRef.current && rootRef.current.scrollIntoView) rootRef.current.scrollIntoView({block: 'start'});
    }, [showChooser]);

    // ── Lo que la pantalla sabe ──
    const tier = subscriptionData.plan_tier;
    const currentPlan = planOf(tier);
    const isCustom = isCustomPlan(tier);
    const planName = currentPlan ? t(currentPlan.nameKey) : (subscriptionData.plan_name || t('agency_subscription.plan'));
    const usage = usageOf(subscriptionData);
    const billingReady = !!(billingData && billingData.available);
    const commAgree = billingReady ? billingData.active_comm_agree : null;
    const client = billingReady ? billingData.client : null;
    const liquidations = billingReady ? (billingData.liquidations || []) : [];
    const price = priceOf(billingData);
    const methodKey = commAgree && commAgree.payment_method ? paymentMethodKey(commAgree.payment_method) : null;
    const subscription = paymentSubscription && paymentSubscription.data;
    // La pastilla habla de la suscripción automática: con otro medio no hay nada que decir de ella.
    const pill = isAutomaticMethod(methodKey) ? subscriptionPillOf(subscription) : null;
    const overdue = overdueOf(liquidations);
    const bankLines = methodKey === 'transfer' ? bankTransferLines(commAgree.bank_transfer_data) : [];
    // El descuento de retención (UD-17), sólo si el convenio lo confirma: si no, todo es UD-16.
    const retention = retentionOf(subscriptionData.active_retention_offer, price);
    const annual = annualOffer(tier, price, retention);
    const nextPlan = isCustom ? null : nextPlanOf(tier);
    const isAdmin = subscriptionData.is_agency_admin;
    const comparison = {currency: (price && price.currency) || String(subscriptionData.currency || 'USD').toUpperCase()};
    if (!['USD', 'ARS'].includes(comparison.currency)) comparison.currency = 'USD';

    // ── Navegación a «Cambiar de plan» ──
    const openChooser = useCallback((eventName) => {
        recordSubscriptionEvent(eventName);
        pushedChooser.current = true;
        window.location.hash = PLAN_CHANGE_HASH.slice(1);
        setShowChooser(true);
    }, []);

    const closeChooser = useCallback(() => {
        if (pushedChooser.current) {
            pushedChooser.current = false;
            window.history.back();
        } else {
            window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        }
        setShowChooser(false);
    }, []);

    // ── Pedidos por el chat (D-2) ──
    const ask = (eventName, message) => {
        recordSubscriptionEvent(eventName);
        openSupportRequest(t, message);
    };
    const askMoreAccounts = (fromNotice) => {
        if (fromNotice) recordSubscriptionEvent('click_more_accounts');
        recordSubscriptionEvent('request_more_accounts');
        const accounts = usage && usage.ad_accounts;
        const quota = accounts && typeof accounts.limit === 'number' && typeof accounts.used === 'number'
            ? {limit: accounts.limit, used: accounts.used} : null;
        // El texto y su medición son de UD-15 (`accountQuota.js`): se reusan, no se copian.
        requestMoreAccounts(t, quota, 'subscription');
    };
    const askCustomPlan = () => ask('request_plan_custom', customPlanMessage(t));

    const sendPlanChange = (target, choice, comment) => {
        const list = listMonthly(target.id, choice.currency, choice.period);
        ask(`request_plan_${target.id}`, planChangeMessage(t, {
            from: planName,
            to: t(target.nameKey),
            period: choice.period,
            price: formatAmount(list, choice.currency, lang),
            comment,
        }));
    };

    const pickPlan = (target, choice) => {
        const direction = planDirection(tier, target.id);
        // Con un plan a medida no hay «subir» ni «bajar» que confirmar: el pedido va directo.
        if (!direction) {
            sendPlanChange(target, choice, '');
            return;
        }
        setRequest({kind: 'plan', plan: target, direction, ...choice, id: `${target.id}-${Date.now()}`});
    };

    const openAnnual = () => {
        recordSubscriptionEvent('click_annual');
        setRequest({kind: 'annual', offer: annual, id: `annual-${Date.now()}`});
    };

    const sendRequest = (comment) => {
        const current = request;
        setRequest(null);
        if (current.kind === 'annual') {
            ask('request_annual', annualMessage(t, {
                plan: planName,
                price: formatAmount(current.offer.monthly, current.offer.currency, lang),
                total: formatAmount(current.offer.total, current.offer.currency, lang),
            }));
            return;
        }
        sendPlanChange(current.plan, current, comment);
    };

    const portalNumbers = (portal) => portal && typeof portal.used === 'number' && typeof portal.limit === 'number';

    const modals = (
        <>
            <RequestConfirmModal key={request ? request.id : 'request-none'} request={request} currentName={planName}
                                 currentPlan={currentPlan} price={price} usage={usage}
                                 onClose={() => setRequest(null)} onSend={sendRequest}/>
            <NotifyPaymentDialog key={notifyFor ? `notify-${notifyFor.id}` : 'notify-none'} liquidation={notifyFor}
                                 onClose={() => setNotifyFor(null)}
                                 onSent={() => { setNotifyFor(null); onLiquidationUpdated(); }}/>
        </>
    );

    if (showChooser) {
        return (
            <div className="subscription-page" ref={rootRef} data-testid="sp-chooser">
                <PlanChooser tier={tier} isCustom={isCustom} price={price} subscriptionCurrency={subscriptionData.currency}
                             usage={usage} retention={retention} onBack={closeChooser} onPickPlan={pickPlan}
                             onRequestMoreAccounts={() => askMoreAccounts(false)}
                             onRequestMoreUsers={(currency) => ask('request_more_users',
                                 moreUsersMessage(t, {currency, lang}))}
                             onRequestMorePortal={(portal) => ask('request_more_portal', portalNumbers(portal)
                                 ? morePortalMessage(t, {used: portal.used, limit: portal.limit})
                                 : t(`${NS}.chat.more_portal_plain`))}
                             onCustomPlan={askCustomPlan}/>
                {modals}
            </div>
        );
    }

    return (
        <div className="subscription-page" ref={rootRef} data-testid="sp-paid">
            <header className="sp-header">
                <h1 className="sp-title">{t(`${NS}.title`)}</h1>
                <p className="sp-lead">{t(`${NS}.lead`)}</p>
            </header>
            {topNotices}
            {overdue && (
                <OverdueNotice key={overdue.oldest.id} overdue={overdue} bankLines={bankLines}
                               onNotify={setNotifyFor}
                               onPaymentProblem={(period) => openSupportRequest(t, paymentProblemMessage(t, {period}))}/>
            )}
            <div className="sp-grid">
                <div className="sp-col sp-col--main">
                    <PlanSummaryCard planName={planName} isCustom={isCustom} price={price} annual={annual}
                                     methodKey={methodKey} paymentSubscription={paymentSubscription}
                                     currentPeriod={currentPeriodOf(liquidations, lang)} billingReady={billingReady}
                                     retention={retention}
                                     onChangePlan={() => openChooser('click_change_plan')} onAnnual={openAnnual}/>
                    {usage && (
                        <UsageCard usage={usage} nextPlan={nextPlan} comparison={comparison}
                                   onSeeNextPlan={() => openChooser('click_bigger_plan')}
                                   onRequestMoreAccounts={() => askMoreAccounts(true)}
                                   onCustomPlan={askCustomPlan}/>
                    )}
                    <PaymentsCard billingData={billingData} methodKey={methodKey} paymentSubscription={paymentSubscription}
                                  isSuperAdmin={isSuperAdmin} refreshing={refreshingBilling}
                                  onRetry={onRetryBilling} onRefresh={onRefreshBilling} onNotify={setNotifyFor}
                                  onLiquidationUpdated={onLiquidationUpdated}/>
                    {billingReady && (client || commAgree) && (
                        <BillingDetailsCard client={client} methodKey={methodKey} pill={pill}
                                            showPaymentLinkEmail={isCouponMethod(methodKey)}
                                            identity={client && client.billing_identity}
                                            currencyKey={currencyKeyOf(price)} bankLines={bankLines}
                                            canEdit={isAdmin} onEmailsSaved={onBillingEmailsSaved}
                                            onRequestIdentity={() => ask('request_billing_identity', billingIdentityMessage(t))}
                                            onRequestMethod={() => ask('request_payment_method', paymentMethodMessage(t, {
                                                method: t(`${NS}.method.${methodKey}`),
                                            }))}/>
                    )}
                </div>
                <aside className="sp-col sp-col--aside">
                    {annual && <AnnualOfferCard planName={planName} offer={annual} onAnnual={openAnnual}/>}
                    <FaqCard/>
                </aside>
                <div className="sp-cancel-slot">
                    <CancelSubscriptionSection variant="link" isFreeTrial={false} subscriptionData={subscriptionData}
                                               cancellationRequest={cancellationRequest}
                                               onCancellationChanged={onCancellationChanged}
                                               onGoToPlans={() => openChooser('click_change_plan')}
                                               onOpenCancel={() => recordSubscriptionEvent('click_cancel')}
                                               onRetentionAccepted={onRetentionAccepted}/>
                </div>
            </div>
            {modals}
        </div>
    );
}
